import {
  Button,
  Callout,
  Checkbox,
  FormLayout,
  GridFieldGroup,
  Heading,
  MaskedInput,
  Paragraph,
  Select,
  TextInput,
  useDialog,
} from '@expressable/syntax-ds';
import { DirectSignupChangeInsuranceDialog } from 'components/Dialog';
import { SubscriberRelationshipEnum } from 'domains/client';
import { useSignupDirectCtx } from 'domains/signupDirect/context';
import { getSubscriberRelationshipOptions } from 'domains/signupDirect/misc/constants';
import { DIRECT_SIGNUP_MIXPANEL_EVENTS } from 'domains/signupDirect/misc/mixpanelTracking';
import {
  ConsultationFormType,
  consultationPaymentMethodIsInsurance,
} from 'domains/signupDirect/schemas/consultationSchema';
import { EvaluationFormType, getInsuranceSchemaType } from 'domains/signupDirect/schemas/evaluationSchema';
import { HeadingCombo } from 'domains/signupDirect/SyntaxComps/HeadingCombo';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useTrack } from 'utils/hooks/useMixPanel';

export const DirectSignupInsuranceConsultationEligibilitySection = () => {
  const { track } = useTrack();
  const { dialogStateProps: changeInsuranceProps, onOpen: onOpenChangeInsurance } = useDialog();
  const { t } = useTranslation();
  const { register, getValues } = useFormContext<ConsultationFormType>();
  const { setValue: setValueConsultationFormType } = useFormContext<ConsultationFormType>();
  const { filters, onReturnToFilters, onSaveIncompleteEvalForm, isResumedLead } = useSignupDirectCtx();
  if (!filters) throw 'Expected filters to be defined';

  const paymentMethod = useWatch<ConsultationFormType, 'billingInfo'>({ name: 'billingInfo' });

  if (consultationPaymentMethodIsInsurance(paymentMethod)) {
    const { insuranceData } = paymentMethod;
    if (!insuranceData) throw 'Expected currentInsuranceData to be defined';
  }

  const hasNoInsuranceInfo =
    useWatch<ConsultationFormType, 'billingInfo.type'>({ name: 'billingInfo.type' }) === 'no-insurance';

  const subscriberRelationshipOptions = getSubscriberRelationshipOptions(t);

  return (
    <>
      <FormLayout.Fields className="mobile-xs-d:gap-e24 tablet-d:gap-e32">
        <HeadingCombo>
          <Heading noDefaultMargins level="h5">
            {t('directSignup.sections.consultation.insurance.title')}
          </Heading>
          <Paragraph variant="secondary" size="md">
            {t('directSignup.sections.consultation.insurance.subtitle')}
          </Paragraph>
        </HeadingCombo>

        <div className="-ml-8">
          <Checkbox
            onChange={event => {
              if (event.target.checked) setValueConsultationFormType('billingInfo.type', 'no-insurance');
              else if (filters.paymentMethod.type === 'insurance')
                setValueConsultationFormType(
                  'billingInfo.type',
                  getInsuranceSchemaType(filters.paymentMethod.insuranceData.insuranceType),
                );
            }}
            variant="plain"
            checked={hasNoInsuranceInfo}
            disabled={filters.paymentMethod.type === 'otherInsurance'}
            id="hasNoInsuranceInfo"
            data-testid="insurance-has-info"
          >
            {t('signup.direct.steps.enterMedicaidInsuranceInfo.notInsuranceAvailable')}
          </Checkbox>
        </div>

        {!hasNoInsuranceInfo && (
          <>
            <div className="flex flex-col gap-e16">
              <TextInput
                data-testid="insurance-plan-readonly-input"
                label={t('directSignup.sections.insurance.labels.insurancePlan')}
                {...register('billingInfo.insuranceData.displayName')}
                disabled
              />
              <Button data-testid="change-insurance-button" variant="link" size="sm" onClick={onOpenChangeInsurance}>
                {t('directSignup.sections.insurance.changeInsurance')}
              </Button>
            </div>

            <GridFieldGroup>
              <TextInput
                data-testid="insurance-member-id-input"
                label={t('directSignup.sections.insurance.labels.memberId')}
                hint={t('directSignup.sections.insurance.hint.memberId')}
                {...register('billingInfo.memberId')}
              />

              <TextInput
                data-testid="insurance-group-id-input"
                label={t('directSignup.sections.insurance.labels.groupId')}
                hint={t('directSignup.sections.insurance.hint.groupId')}
                {...register('billingInfo.groupId')}
              />
            </GridFieldGroup>

            {paymentMethod.type === 'insurance-commercial' && (
              <Callout className="w-full" type="info">
                {t('directSignup.sections.insurance.labels.subscriber.verifySubscriberInfoCopy')}
              </Callout>
            )}

            {paymentMethod.type === 'insurance-commercial' && (
              <GridFieldGroup>
                <Select
                  label={t('directSignup.sections.insurance.labels.subscriber.relationshipToClient')}
                  name="billingInfo.subscriber.relationShipToClient"
                  options={subscriberRelationshipOptions}
                />

                {paymentMethod.subscriber?.relationShipToClient !== SubscriberRelationshipEnum.Self && (
                  <>
                    <MaskedInput
                      data-testid="insurance-subscriber-dob-input"
                      label={t('directSignup.sections.insurance.labels.subscriber.dateOfBirth')}
                      {...register('billingInfo.subscriber.dateOfBirth')}
                      mask="MM/DD/YYYY"
                      showMask
                      type="tel"
                      replacement={{
                        M: /[0-1]|[0-9]/,
                        D: /[0-3]|[0-9]/,
                        Y: /\d/,
                      }}
                    />
                    <TextInput
                      label={t('directSignup.sections.insurance.labels.subscriber.firstName')}
                      {...register('billingInfo.subscriber.firstName')}
                    />
                    <TextInput
                      label={t('directSignup.sections.insurance.labels.subscriber.lastName')}
                      {...register('billingInfo.subscriber.lastName')}
                    />
                  </>
                )}
              </GridFieldGroup>
            )}
          </>
        )}
      </FormLayout.Fields>

      <DirectSignupChangeInsuranceDialog
        {...changeInsuranceProps}
        onConfirm={() => {
          const incompleteForm = getValues();
          /**this is expected because we are saving consultation form data in the incomplete evaluation form
           *  @ts-expect-error (they aren't 100% compatible but should work for now) @TODO refactor*/
          onSaveIncompleteEvalForm(incompleteForm as EvaluationFormType);
          onReturnToFilters();
          track(DIRECT_SIGNUP_MIXPANEL_EVENTS.CHANGE_PAYMENT_METHOD.FROM_INSURANCE, { isResumedLead });
        }}
      />
    </>
  );
};
